import type { IdFieldType, TimestampFieldsType } from 'bc-model';

function identity<T>(obj: T): T {
  return obj;
}

const truthy = <T>(obj: T | false | undefined | null | '' | 0): obj is T => !!obj;

const nonNullish = <T>(obj: T | null | undefined): obj is T => obj !== null && obj !== undefined;

function falsy(obj: unknown): boolean {
  return !obj;
}

type Predicate<T> = (v: T, i: number, a: T[]) => boolean;

const not = <T>(fn: Predicate<T>): Predicate<T> => {
  return (v, i, a) => !fn(v, i, a);
};

function groupByUniqueId(idProp: string = 'id') {
  return (acc: any, item: any): any => ({ ...acc, [item[idProp]]: item });
}

function groupByKey<T>(items: T[], key: keyof T | ((arg: T) => string)): { [key: string]: T } {
  const groupedItems = {};
  items.forEach((item) => {
    groupedItems[typeof key === 'function' ? key(item) : String(item[key])] = item;
  });

  return groupedItems;
}

function sumAcc(sum = 0, next: number): number {
  return sum + next;
}

function range(max: number, min: number = 0, step: number = 1): number[] {
  if (step <= 0) {
    throw new Error('Step must be a positive number');
  }

  if (min <= max) {
    const maxInclusive = max + 1;
    const stepsBetween = Math.ceil((maxInclusive - min) / step);
    const steps = Object.keys([...Array(stepsBetween)]).map((k) => +k * step);
    return steps.map((s) => min + s);
  }
  return [];
}

type Tally<T extends string | number | symbol> = Partial<Record<T, number>>;

const tally = <T extends string | number | symbol>(acc: Tally<T>, value: T): Tally<T> => {
  acc[value] = (acc[value] ?? 0) + 1;
  return acc;
};

const toLatestUniqueByIdRecord = <T extends IdFieldType & Pick<TimestampFieldsType, 'updatedAt'>>(
  acc: Record<T['id'], T>,
  curr: T
): Record<T['id'], T> => {
  const existing: T | undefined = acc[curr.id];
  const currentIsLatest =
    existing && new Date(curr.updatedAt).getTime() > new Date(existing.updatedAt).getTime();
  if (!existing || currentIsLatest) {
    acc[curr.id] = curr;
  }
  return acc;
};

export type { Tally };
export {
  identity,
  groupByKey,
  groupByUniqueId,
  range,
  sumAcc,
  truthy,
  falsy,
  nonNullish,
  not,
  tally,
  toLatestUniqueByIdRecord,
};
